/**
 * v7 design-system primitives.
 * Pure HTML/CSS components (no AntD chrome) matching docs/specs/HuanKe-CRM-界面demo-v7.html.
 */
import { ReactNode, CSSProperties, useEffect, useState } from 'react';
import { useCountUp } from '../lib/hooks';

/* ─── Pill (status chip) ─────────────────────────────────── */
export type PillTone = 'primary' | 'success' | 'warning' | 'danger' | 'default' | 'purple';
export function Pill({ tone = 'default', children }: { tone?: PillTone; children: ReactNode }) {
  return (
    <span className={`pill pill-${tone}`}>
      <span className="dot" />
      {children}
    </span>
  );
}

/* ─── Customer flag (S/A/B/C/旅程范例) ───────────────────── */
export function CustFlag({ kind, label }: { kind: 's' | 'a' | 'b' | 'c' | 'journey'; label?: string }) {
  const map: Record<string, string> = { s: 'S', a: 'A', b: 'B', c: 'C', journey: '旅程范例' };
  return <span className={`cust-name-flag flag-${kind}`}>{label ?? map[kind]}</span>;
}

/* ─── Cust cell (avatar + name + meta) ───────────────────── */
const AVATAR_COLORS = [1, 2, 3, 4, 5, 6, 7, 8];
export function avatarSlot(id: number | string) {
  const n = typeof id === 'number' ? id : Array.from(String(id)).reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[Math.abs(n) % 8];
}

/** P6 头像统一组件 — 上传了图片显示 <img>，否则降级到色块 + 首字 */
export function Avatar({
  userId, src, name, size = 'md',
}: {
  userId: number | string;
  src?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const [err, setErr] = useState(false);
  const px = size === 'sm' ? 24 : size === 'lg' ? 80 : 36;
  const fontSize = size === 'sm' ? 11 : size === 'lg' ? 28 : 14;
  if (src && !err) {
    return (
      <img src={src}
           alt={name ?? ''}
           onError={() => setErr(true)}
           style={{
             width: px, height: px, borderRadius: '50%',
             objectFit: 'cover', flexShrink: 0,
             border: '1px solid var(--border-default)',
           }} />
    );
  }
  const slot = avatarSlot(userId);
  const first = (name ?? '?').slice(0, 1);
  return (
    <div className={`cust-avatar c${slot}`}
         style={{ width: px, height: px, fontSize, flexShrink: 0 }}>
      {first}
    </div>
  );
}
export function CustCell({
  id, name, meta, flag, flagLabel,
}: {
  id: number | string;
  name: string;
  meta?: ReactNode;
  flag?: 's' | 'a' | 'b' | 'c' | 'journey';
  flagLabel?: string;
}) {
  const slot = avatarSlot(id);
  const first = (name || '?').slice(0, 1);
  return (
    <div className="cust-cell">
      <div className={`cust-avatar c${slot}`}>{first}</div>
      <div className="cust-info">
        <div className="cust-name">
          {name}
          {flag && <CustFlag kind={flag} label={flagLabel} />}
        </div>
        {meta && <div className="cust-meta">{meta}</div>}
      </div>
    </div>
  );
}

/* ─── Stat card ──────────────────────────────────────────── */
export function StatCard({
  label, value, unit, delta, deltaDir, meta, valueColor, prefix, suffix,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  delta?: ReactNode;
  deltaDir?: 'up' | 'down';
  meta?: ReactNode;
  valueColor?: 'warning' | 'success' | 'danger' | 'primary';
  /** Optional formatter — if value is a number, count-up animation runs; prefix/suffix wrap the rendered number. */
  prefix?: string;
  suffix?: string;
}) {
  const colorVar = valueColor
    ? { color: `var(--${valueColor === 'warning' ? 'warning-text' : valueColor === 'success' ? 'success-text' : valueColor === 'danger' ? 'danger-text' : 'primary'})` }
    : undefined;
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value-row">
        <AnimatedValue value={value} prefix={prefix} suffix={suffix} colorStyle={colorVar} />
        {unit && <span className="stat-unit">{unit}</span>}
        {delta && <span className={`stat-delta ${deltaDir ?? 'up'}`}>{delta}</span>}
      </div>
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}

/** Number cell with count-up. If value isn't numeric, render as-is. */
function AnimatedValue({
  value, prefix, suffix, colorStyle,
}: {
  value: ReactNode;
  prefix?: string;
  suffix?: string;
  colorStyle?: CSSProperties;
}) {
  const numeric = typeof value === 'number' && isFinite(value);
  if (!numeric) {
    return <span className="stat-value" style={colorStyle}>{prefix}{value}{suffix}</span>;
  }
  return <CountUpSpan target={value as number} prefix={prefix} suffix={suffix} colorStyle={colorStyle} />;
}

function CountUpSpan({
  target, prefix, suffix, colorStyle,
}: {
  target: number;
  prefix?: string;
  suffix?: string;
  colorStyle?: CSSProperties;
}) {
  const v = useCountUp(target);
  // Use the same precision as the target (integer stays integer)
  const display = Number.isInteger(target) ? Math.round(v).toLocaleString() : v.toFixed(1);
  return <span className="stat-value" style={colorStyle}>{prefix}{display}{suffix}</span>;
}

export function StatsRow({ children }: { children: ReactNode }) {
  return <div className="stats-row">{children}</div>;
}

/* ─── Filter bar (tabs) ──────────────────────────────────── */
export interface FilterTab { key: string; label: string; count?: number | null }
export function FilterBar({
  tabs, active, onChange, right,
}: {
  tabs: FilterTab[];
  active: string;
  onChange: (k: string) => void;
  right?: ReactNode;
}) {
  return (
    <div className="filter-bar">
      <div className="filter-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`filter-tab ${active === t.key ? 'active' : ''}`}
            onClick={() => onChange(t.key)}
          >
            {t.label}
            {t.count != null && <span className="filter-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>
      {right && <div className="filter-bar-right">{right}</div>}
    </div>
  );
}

/* ─── Page header ────────────────────────────────────────── */
export function PageHeader({
  breadcrumb, title, tag, subtitle, actions,
}: {
  breadcrumb?: string[];
  title: string;
  tag?: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      {breadcrumb && breadcrumb.length > 0 && (
        <div className="page-breadcrumb">
          {breadcrumb.map((b, i) => (
            <span key={i}>
              {i > 0 && <span className="page-breadcrumb-sep">/</span>}
              <a>{b}</a>
            </span>
          ))}
        </div>
      )}
      <div className="page-title-row">
        <div className="page-title">{title}</div>
        {tag && <span className="page-tag">{tag}</span>}
        {subtitle && <span className="page-subtitle">{subtitle}</span>}
        {actions && <div className="page-actions">{actions}</div>}
      </div>
    </div>
  );
}

/* ─── Btn (matches v7 demo styles, alternative to AntD button when we want pixel match) ─── */
export function Btn({
  kind = 'default', onClick, children, icon, type, disabled,
}: {
  kind?: 'primary' | 'default' | 'text' | 'danger';
  onClick?: () => void;
  children: ReactNode;
  icon?: ReactNode;
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  return (
    <button
      type={type ?? 'button'}
      className={`btn btn-${kind}${disabled ? ' btn-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      {children}
    </button>
  );
}

/* ─── Table wrap ─────────────────────────────────────────── */
export function TableWrap({ children }: { children: ReactNode }) {
  return <div className="table-wrap">{children}</div>;
}

export function TableFooter({
  total, page, pageSize, onPage,
}: {
  total: number;
  page: number;
  pageSize: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const around = [page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages);
  return (
    <div className="table-footer">
      <div>共 {total} 条</div>
      <div className="pagination">
        <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
        {!around.includes(1) && (
          <>
            <button className={`page-btn ${page === 1 ? 'active' : ''}`} onClick={() => onPage(1)}>1</button>
            {around[0] > 2 && <span style={{ padding: '0 4px', color: 'var(--text-3)' }}>…</span>}
          </>
        )}
        {around.map((n) => (
          <button key={n} className={`page-btn ${page === n ? 'active' : ''}`} onClick={() => onPage(n)}>{n}</button>
        ))}
        {!around.includes(pages) && pages > 1 && (
          <>
            {around[around.length - 1] < pages - 1 && <span style={{ padding: '0 4px', color: 'var(--text-3)' }}>…</span>}
            <button className={`page-btn ${page === pages ? 'active' : ''}`} onClick={() => onPage(pages)}>{pages}</button>
          </>
        )}
        <button className="page-btn" disabled={page >= pages} onClick={() => onPage(page + 1)}>›</button>
      </div>
    </div>
  );
}

/* ─── AI box ─────────────────────────────────────────────── */
export function AiBox({ title = 'AI 提示', children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <div className="ai-box">
      <div className="ai-box-head" style={{ width: '100%' }}>
        <svg className="ai-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L9 9l-7 1 5 5-1 7 6-3 6 3-1-7 5-5-7-1z" />
        </svg>
        {typeof title === 'string' ? <span className="ai-box-title">{title}</span> : title}
      </div>
      <div className="ai-box-text">{children}</div>
    </div>
  );
}

/* ─── Journey CTA ────────────────────────────────────────── */
export function JourneyCta({
  title = '查看完整客户旅程',
  sub = '从 L1 触达到 L8 服务期，全程可视化',
  onClick,
}: {
  title?: string;
  sub?: string;
  onClick: () => void;
}) {
  return (
    <div className="journey-cta" onClick={onClick}>
      <div className="journey-cta-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12c0-5 4-9 9-9s9 4 9 9-4 9-9 9-9-4-9-9z" />
          <path d="M12 7v5l3 2" />
        </svg>
      </div>
      <div className="journey-cta-text">
        <div className="journey-cta-title">{title}</div>
        <div className="journey-cta-sub">{sub}</div>
      </div>
      <div className="journey-cta-arrow">›</div>
    </div>
  );
}

/* ─── Modal shell ────────────────────────────────────────── */
export function Modal({
  open, title, onClose, size, bodyClass, children, footer,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  size?: 'large';
  bodyClass?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // Close on Escape, regardless of where focus is. Each open Modal registers its own
  // listener so the most-recent modal closes first when nested.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className={`modal-overlay open`} onClick={onClose}>
      <div className={`modal ${size === 'large' ? 'large' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className={`modal-body ${bodyClass ?? ''}`}>{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ─── 健康度阈值单一真相源（条6 归一）─────────────────────────
 *  全站健康度(0-100)颜色档：>=70 绿(健康) / >=40 黄(警告) / <40 红(危急)。
 *  此前 AI 健康度评估卡按 risk_level 上色、缺省永远绿(50分也显绿)；小徽标/进度条/公海都已按 >=70 绿。
 *  统一到本函数：各处保留自己的色 token(InlineProgress 用 --success、AI 卡用 --success-text、公海用 --warn)，只把【阈值判定】收口到这里。 */
export type HealthLevel = 'green' | 'yellow' | 'red';
export function healthLevel(score?: number | null): HealthLevel {
  const h = Number(score ?? 0);
  if (h >= 70) return 'green';
  if (h >= 40) return 'yellow';
  return 'red';
}

/* ─── Inline progress bar (table) ────────────────────────── */
export function InlineProgress({ value, color }: { value: number; color?: string }) {
  const tone = color ?? { green: 'var(--success)', yellow: 'var(--warning)', red: 'var(--danger)' }[healthLevel(value)];
  return (
    <div className="tbl-progress">
      <div className="tbl-progress-bar">
        <div className="tbl-progress-fill" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: tone }} />
      </div>
      <span className="tbl-progress-val">{value}</span>
    </div>
  );
}

/* ─── Stage pill mapping (规格 3.1 节定义的完整 10 阶段) ─────
 *  数据库 customers.current_stage 枚举：L0/L1/L2/L3/L4/L5a/L5b/L6/L7/L8
 *  L5 是过渡期前端简写，实际数据只会出现 L5a (报价已发出) / L5b (报价已接受)。
 *  保留 L5 映射只是为了向后兼容旧数据。 */
export interface StageDef {
  key: string;
  /** 不带前缀的短名（"触达""破冰"），用于 tab 标签 */
  short: string;
  /** 带阶段号的长名（"L1 触达"），用于 pill */
  label: string;
  tone: PillTone;
}
export const STAGES_FULL: StageDef[] = [
  { key: 'L0',  short: '待加微信',   label: 'L0 待加微信',   tone: 'default' },
  { key: 'L1',  short: '触达',       label: 'L1 触达',       tone: 'default' },
  { key: 'L2',  short: '破冰',       label: 'L2 破冰',       tone: 'primary' },
  { key: 'L3',  short: '挖需',       label: 'L3 挖需',       tone: 'primary' },
  { key: 'L4',  short: '深聊',       label: 'L4 深聊',       tone: 'primary' },
  { key: 'L5a', short: '报价已发出', label: 'L5a 报价已发出', tone: 'warning' },
  { key: 'L5b', short: '报价已接受', label: 'L5b 报价已接受', tone: 'warning' },
  { key: 'L6',  short: '逼单',       label: 'L6 逼单',       tone: 'danger' },
  { key: 'L7',  short: '成交',       label: 'L7 成交',       tone: 'success' },
  { key: 'L8',  short: '服务期',     label: 'L8 服务期',     tone: 'success' },
];
export const STAGE_META: Record<string, { tone: PillTone; label: string }> = {
  ...Object.fromEntries(STAGES_FULL.map((s) => [s.key, { tone: s.tone, label: s.label }])),
  // 权威 10 档已含 L5a/L5b；旧 'L5' 残留已清（全清后无 L5 数据）。未知 stage 由调用处 ?? 兜底显原值。
};

/** inc6 阶段字典统一：有序 10 档键 + 短名映射。漏斗/旅程时间轴等各页统一从此取，
 *  杜绝各处硬编 ['L1'..'L8'] + 合并 L5a/L5b/漏 L0 的字典漂移。顺序=L0,L1,L2,L3,L4,L5a,L5b,L6,L7,L8。 */
export const STAGE_KEYS = STAGES_FULL.map((s) => s.key);
export const STAGE_SHORT: Record<string, string> = Object.fromEntries(STAGES_FULL.map((s) => [s.key, s.short]));

export const STATUS_META: Record<string, { tone: PillTone; label: string }> = {
  probation: { tone: 'warning', label: '试用期' },
  active: { tone: 'primary', label: '正式持有' },
  dealt: { tone: 'success', label: '已成交' },
  in_service: { tone: 'success', label: '服务中' },
  pool: { tone: 'default', label: '公海' },
  archived: { tone: 'default', label: '归档' },
  deleted: { tone: 'default', label: '已删除' },
};

/** ux11 发票类型中文化共享出口：invoice=发票 / none=不需要开 / 其余=收据。
 *  口径与各页（FinanceWorkbench、AllListPages 类型列）一致，不扩 special 枚举。 */
export function invTypeLabel(type: string): string {
  if (type === 'invoice') return '发票';
  if (type === 'none') return '不需要开';
  return '收据';
}

/* ─── Funnel row (used in dashboard) ─────────────────────── */
export function FunnelRow({
  stage, label, count, rate, max,
}: {
  stage: string;
  label: string;
  count: number;
  rate: number;
  max: number;
}) {
  const stageLower = stage.toLowerCase().replace(/[a-z]$/, '');
  const widthPct = max > 0 ? Math.max(6, (count / max) * 100) : 6;
  return (
    <div className="funnel-row">
      <div className="funnel-label">
        <span className="funnel-label-num">{stage}</span>
        <span>{label}</span>
      </div>
      <div className="funnel-bar-wrap">
        <div className={`funnel-bar ${stageLower}`} style={{ width: `${widthPct}%` }}>{count}</div>
      </div>
      <div className="funnel-rate" data-tip={`${count} 人 · 占最高阶段的 ${rate}%`}>{rate}%</div>
    </div>
  );
}

/* ─── Stage dots — 7-step progress mini-bar after a pill ─── */
const STAGE_ORDER = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7'];
const STAGE_TIPS: Record<string, string> = {
  L1: 'L1 触达', L2: 'L2 破冰', L3: 'L3 挖需', L4: 'L4 深聊',
  L5: 'L5 报价', L6: 'L6 逼单', L7: 'L7 成交',
};
export function StageDots({ current }: { current: string }) {
  /** Normalize L5a / L5b → L5 for the 7-stage compact view. */
  const norm = current?.replace(/^(L\d+)[a-z]+$/, '$1') ?? 'L1';
  const idx = STAGE_ORDER.indexOf(norm);
  return (
    <span className="stage-dots">
      {STAGE_ORDER.map((s, i) => {
        const cls = i < idx ? 'done'
          : i === idx ? (idx === 6 ? 'deal' : 'current')
          : '';
        return <span key={s} className={`stage-dot ${cls}`} data-tip={STAGE_TIPS[s]} />;
      })}
    </span>
  );
}

/* ─── Empty hint (new-user "click + to start" prompt) ────── */
export function EmptyHint({
  children, kbd, arrow = true,
}: {
  children: ReactNode;
  kbd?: string;
  arrow?: boolean;
}) {
  return (
    <div className="empty-hint">
      <div className="empty-hint-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
      </div>
      <div className="empty-hint-text">
        {children}
        {kbd && <> · 或按 <kbd>{kbd}</kbd></>}
      </div>
      {arrow && <span className="empty-hint-arrow">→</span>}
    </div>
  );
}

/* ─── Home card shell ────────────────────────────────────── */
export function HomeCard({
  title, action, children, bodyStyle,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  bodyStyle?: CSSProperties;
}) {
  return (
    <div className="home-card">
      <div className="home-card-head">
        <div className="home-card-title">{title}</div>
        {action && <span className="home-card-action">{action}</span>}
      </div>
      <div className="home-card-body" style={bodyStyle}>{children}</div>
    </div>
  );
}
